/**
 * Persist a settings change and apply it to the live scene, one path per
 * setting.
 *
 * Every source of a change goes through the same function — the popover slider,
 * the `M` key, a toggle in the in-flight controls row — so storage and the
 * running scene can never disagree about what the player asked for. The
 * counterpart of `persisted-settings.ts`, which owns the storage half and
 * knows nothing about a scene.
 *
 * Most are taken **live** rather than on the next sortie: audio is a mixer,
 * AD-012's propeller disc is cosmetic, and AD-007n's stick shaping is an input
 * setting the scene absorbs by leaving the axes exactly where they stand (a
 * mid-turn snap to the held keys would put in an input the player did not make).
 * The one that is not is `applySimulation`, and its own entry says why.
 */

import type { AudioSettings } from "../audio/settings.ts";
import type { EffectsSettings } from "./effects-settings.ts";
import type { FlightScene } from "./flight-scene-api.ts";
import type { KillCamPlacement } from "./killcam-layout.ts";
import type { PersistedFlightSettings } from "./persisted-settings.ts";
import type { StickSettings } from "./stick-settings.ts";
import type { BailSettings } from "./bail-settings.ts";
import type { SimSettings } from "./sim-settings.ts";

export interface LiveSettings {
  applyAudio(next: AudioSettings): void;
  applyEffects(next: EffectsSettings): void;
  applyStick(next: StickSettings): void;
  /** AD-021. Storage and the running scene, so switching it off makes `H` inert without leaving the cockpit. */
  applyBail(next: BailSettings): void;
  /**
   * ENHANCED SIMULATION (I12) — **storage only, and that is the setting's own
   * shape rather than a gap here.**
   *
   * A sortie's ships are deployed once, when its run is built against the
   * region's water (`packages/engine`'s `mission-spec.ts`). Adding one to a
   * fight already under way would put a hull, a battery and a belt into a
   * hashed state mid-tick, which is a lockstep desync and a replay tape that no
   * longer describes its own recording. So the bit is written now and the sea
   * has ships in it from the next sortie, exactly as `sim-settings.ts` says.
   */
  applySimulation(next: SimSettings): void;
  /** One frame of a kill-cam window drag: signal and scene, no storage write. */
  moveKillCam(next: KillCamPlacement): void;
  /** The release that ends one: signal, scene *and* storage. */
  commitKillCam(next: KillCamPlacement): void;
}

export function createLiveSettings(
  settings: PersistedFlightSettings,
  scene: () => FlightScene | undefined,
): LiveSettings {
  return {
    applyAudio(next) {
      settings.writeAudio(next);
      scene()?.setAudioSettings(next);
    },
    applyEffects(next) {
      settings.writeEffects(next);
      scene()?.setEffectsSettings(next);
    },
    applyStick(next) {
      settings.writeStick(next);
      scene()?.setStickShaping(next.shaping);
    },
    applyBail(next) {
      settings.writeBail(next);
      scene()?.setBailEnabled(next.enabled);
    },
    applySimulation(next) {
      settings.writeSimulation(next);
    },
    moveKillCam(next) {
      settings.moveKillCamPlacement(next);
      scene()?.setKillCamPlacement(next);
    },
    commitKillCam(next) {
      settings.writeKillCamPlacement(next);
      scene()?.setKillCamPlacement(next);
    },
  };
}
